import { css } from 'lit'
import { fadeTransition } from '../../foundations/styles/index.js'

export const menuStyles = css`
  .menu-overlay {
    position: fixed;
    top: var(--header-height); /* Start below the header */
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999; /* Behind the header */
    background: transparent;
    visibility: hidden;
    transition: visibility 0s linear 0.3s;
    pointer-events: none;
  }

  .menu-overlay::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--menu-overlay-bg);
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    pointer-events: none;
  }

  .menu-overlay.open {
    pointer-events: auto;
    visibility: visible;
    transition: visibility 0s linear 0s;
  }

  .menu-overlay.open::before {
    opacity: 1;
  }

  .menu-container {
    position: relative;
    z-index: 1;
    width: 100%;
    max-height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
    padding: 20px 24px 32px;
    max-width: none;
    margin: 0;
    background: var(--menu-bg);
    border-top: var(--menu-border-top);
  }

  .menu-overlay.open .menu-container {
    transform: translateY(0);
  }

  .menu-desktop-content,
  .menu-mobile-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .menu-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 32px;
  }


  .menu-logo {
    width: 24px;
    height: 22px;
  }

  .menu-desktop-content {
    display: none;
  }

  .menu-mobile-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
  }

  .menu-empty-state {
    border: 1px solid var(--menu-divider-color);
    border-radius: 8px;
    padding: 16px;
    background: #faf9f6;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .menu-empty-state.loading {
    border-color: #d9e2b6;
    background: #f7faee;
  }

  .menu-empty-state.error {
    border-color: #f0d7d2;
    background: #fff7f5;
  }

  .menu-empty-state-title {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    color: #1f1b16;
  }

  .menu-empty-state-subtitle {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    color: #4f4a45;
  }

  .menu-section {
    display: flex;
    border-bottom: 1px solid var(--menu-section-border-color);
  }

  .menu-section:last-child {
    border-bottom: none;
  }

  .section-title {
    display: block;
    width: 100%;
    padding: 16px 0;
    font-family: 'HCo Mercury Text G2', Georgia, 'Times New Roman', serif;
    font-weight: 400;
    font-size: 22px;
    line-height: 1.15;
    color: var(--menu-section-title-color);
    margin: 0;
    text-decoration: none;
    ${fadeTransition}
  }

  .menu-items {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .menu-item {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.45;
    color: var(--menu-item-color);
    text-decoration: none;
    transition: color 0.2s ease-in-out;
  }

  .menu-item:hover {
    color: var(--menu-item-hover-color);
  }

  /* Buttons rendered as menu items (Sign out) must line up with the
     anchor menu items — reset the native button box and left-align the label. */
  button.menu-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
  }

  /* Signed-out account actions rendered as full-width buttons (Create Account /
     Sign In), matching the reference menu. */
  .menu-auth-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 4px;
  }

  .menu-auth-button {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    border-radius: 4px;
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    text-decoration: none;
    cursor: pointer;
    box-sizing: border-box;
    transition: background-color 0.2s ease-in-out;
  }

  .menu-auth-button--primary {
    background: #333333;
    border: 1px solid #333333;
    color: #ffffff;
  }

  .menu-auth-button--primary:hover {
    background: #2a2a2a;
  }

  .menu-auth-button--secondary {
    background: #ffffff;
    border: 1px solid #333333;
    color: #333333;
  }

  .menu-auth-button--secondary:hover {
    background: #f5f5f5;
  }

  .menu-item.featured {
    color: var(--menu-accent-color);
    font-weight: 500;
  }

  .menu-item.date {
    /* Avenir Heavy at its native weight (DEV-1182) — matches the nav fix so the
       event date labels don't faux-bold inconsistently across sites. */
    font-family: 'Avenir LT W01_85 Heavy1475544', 'Avenir', sans-serif;
    font-size: 11px;
    color: var(--menu-accent-color);
    font-weight: 400;
    font-synthesis: none;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .menu-item.subtitle {
    font-size: 12px;
    color: #999999;
    margin-top: -8px;
    line-height: 1.4;
  }

  .promotion-section {
    border-top: 1px solid var(--menu-section-border-color);
    padding-top: 16px;
    margin-top: 16px;
  }

  .promotion-title {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: var(--menu-accent-color);
    margin-bottom: 8px;
  }

  .promotion-subtitle {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-weight: 400;
    font-size: 12px;
    color: var(--menu-promo-color);
  }

  /* Accordion Styles */
  .accordion-section {
    border-bottom: 1px solid var(--menu-section-border-color);
  }

  .accordion-section:last-child {
    border-bottom: none;
  }

  .accordion-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 16px 0;
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'HCo Mercury Text G2', Georgia, 'Times New Roman', serif;
    font-weight: 400;
    font-size: 22px;
    line-height: 1.15;
    color: var(--menu-section-title-color);
    transition: color 0.2s ease-in-out;
  }

  .accordion-toggle:hover {
    color: var(--menu-item-hover-color);
  }

  .toggle-label {
    display: block;
    text-align: left;
  }

  .toggle-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    color: var(--menu-section-title-color);
    transition: transform 0.3s ease-in-out;
    margin-left: 8px;
    flex-shrink: 0;
  }

  .toggle-icon-svg {
    width: 12px;
    height: 12px;
    display: block;
  }

  .toggle-icon-svg path {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .accordion-toggle.expanded .toggle-icon {
    transform: rotate(180deg);
  }

  .accordion-content {
    max-height: 0;
    overflow: hidden;
    visibility: hidden;
    padding-bottom: 0;
    transition: max-height 0.3s ease-in-out, visibility 0s linear 0.3s;
  }

  .accordion-content.expanded {
    max-height: 1000px;
    overflow: visible;
    visibility: visible;
    padding-bottom: 16px;
    transition: max-height 0.3s ease-in-out, visibility 0s linear 0s;
  }

  .menu-item-wrapper {
    padding-bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .menu-item-wrapper:last-child {
    padding-bottom: 0;
  }

  .menu-mobile-divider {
    width: 20px;
    border-top: 1px solid var(--menu-accent-border-color);
    margin: 0 0 16px;
  }

  .menu-item-wrapper.promotion {
    gap: 6px;
  }

  .menu-item.promo-title {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--menu-accent-color);
    line-height: 1.4;
  }

  .menu-item.promo-subtitle {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: var(--menu-promo-color);
    line-height: 1.4;
  }

  .menu-item.event-link {
    line-height: 1.4;
  }

  @media (min-width: 1025px) {
    .menu-container {
      box-shadow: 0 18px 40px rgba(16, 11, 0, 0.08);
      padding: 24px 24px 24px;
      max-height: calc(100vh - var(--header-height, 56px));
    }

    .menu-mobile-content {
      display: none;
    }

    .menu-desktop-content {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 0;
    }

    .menu-desktop-content > .menu-empty-state {
      grid-column: 1 / -1;
      margin-top: 8px;
    }

    .menu-desktop-section {
      min-width: 0;
      padding: 32px 24px 16px;
      border-left: 1px solid var(--menu-divider-color);
    }

    .menu-desktop-section:last-child {
      border-right: 1px solid var(--menu-divider-color);
    }

    .menu-desktop-title {
      display: inline-block;
      margin-bottom: 20px;
      font-family: 'HCo Mercury Text G2', Georgia, 'Times New Roman', serif;
      font-size: 22px;
      line-height: 1.2;
      color: var(--menu-desktop-section-title-color);
      text-decoration: none;
    }

    .menu-desktop-items {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .menu-desktop-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .menu-desktop-link {
      font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: var(--menu-desktop-link-color);
      text-decoration: none;
      ${fadeTransition}
    }

    .menu-desktop-link:hover {
      color: var(--menu-desktop-link-hover-color);
    }

    .menu-desktop-link.featured {
      color: var(--menu-accent-color);
    }

    .menu-desktop-link.event-link {
      max-width: 220px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .menu-desktop-meta {
      /* Avenir Heavy at its native weight (DEV-1182) — see .menu-item.date. */
      font-family: 'Avenir LT W01_85 Heavy1475544', 'Avenir', sans-serif;
      font-size: 11px;
      font-weight: 400;
      font-synthesis: none;
      letter-spacing: 1.6px;
      color: var(--menu-desktop-meta-color);
      text-transform: uppercase;
    }

    .menu-desktop-divider {
      width: 20px;
      border-top: 1px solid var(--menu-accent-border-color);
      margin: 4px 0 2px;
    }

    .menu-desktop-promo-title {
      font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
      font-size: 12px;
      color: var(--menu-promo-color);
      line-height: 1.4;
      margin-top: 6px;
    }

    .menu-desktop-promo-subtitle {
      font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
      font-size: 12px;
      color: var(--menu-promo-color);
      line-height: 1.4;
    }
  }

  /* "Stay in Touch" email signup pinned to the bottom of the open menu.
     Mirrors the footer's email-signup styling (rectangular input + small
     outlined uppercase Submit) so the two match across the menu and footer. */
  .menu-email-signup {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.25rem;
    padding: 1.5rem 2.5rem;
    border-top: 1px solid var(--menu-section-border-color);
    background-color: var(--menu-bg);
  }

  .menu-email-signup-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
  }

  .menu-email-signup-icon {
    flex-shrink: 0;
    fill: #ed6234;
  }

  .menu-email-signup-title {
    margin: 0;
    font-family: 'HCo Mercury Text G2', Georgia, serif;
    font-size: 1.25rem;
    line-height: 1.3;
    color: var(--menu-section-title-color);
  }

  .menu-email-signup-form {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    width: 100%;
    max-width: 100%;
  }

  .menu-email-signup-input {
    flex: 1;
    border: 1px solid #e5e7eb;
    color: #100b00;
    background: inherit;
    border-radius: 0.175rem;
    font-size: 0.75rem;
    line-height: 1rem;
    text-indent: 0.75rem;
    height: 1.75rem;
    font-family: inherit;
  }

  .menu-email-signup-input::placeholder {
    color: #949494;
  }

  .menu-email-signup-button {
    display: inline-flex;
    background: inherit;
    color: #647a1c;
    border: 1px solid #647a1c;
    border-radius: 0.175rem;
    text-decoration-line: none;
    cursor: pointer;
    font-size: 0.6rem;
    line-height: 1rem;
    text-transform: uppercase;
    height: 1.75rem;
    justify-content: center;
    align-items: center;
    padding: 0 0.5rem;
    font-family:
      'Avenir LT W01_85 Heavy1475544',
      Avenir,
      sans-serif;
    letter-spacing: 0.1em;
    font-weight: 700;
    opacity: 0.7;
    flex-shrink: 0;
    width: 4.5rem;
  }

  @media (min-width: 768px) {
    .menu-email-signup {
      flex-direction: row;
      max-width: 64rem;
      margin: 0 auto;
    }

    .menu-email-signup-form {
      width: 50%;
    }

    .menu-email-signup-title {
      margin-right: 1rem;
    }
  }
`